'use client';

import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { applyFallbackImageOnError, FALLBACK_IMAGE_URL, getObraImageUrl } from '@/utils/image-utils';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { generateWorkLink } from '@/utils/utils';

/** Cor fixa da secção (favoritos) — igual à app. */
const FAVORITAS_SECTION_AMBER = '#fbbf24';

interface FavoritadasRecentesProps {
    formatoId?: number;
}

/** Scroll horizontal com arrastar pelo rato (clique + arrastar), sem roubar o clique se não houver movimento. */
function useHorizontalDragScroll<T extends HTMLElement>(active: boolean) {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        if (!active) return;
        const el = ref.current;
        if (!el) return;

        let isDown = false;
        let startX = 0;
        let startScrollLeft = 0;
        let moved = false;

        const onDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            isDown = true;
            moved = false;
            startX = e.clientX;
            startScrollLeft = el.scrollLeft;
        };

        const onMove = (e: MouseEvent) => {
            if (!isDown) return;
            const dx = e.clientX - startX;
            if (Math.abs(dx) > 5) {
                moved = true;
                el.scrollLeft = startScrollLeft - dx;
            }
        };

        const endDrag = () => {
            if (!isDown) return;
            isDown = false;
            if (moved) {
                const block = (ev: MouseEvent) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    window.removeEventListener('click', block, true);
                };
                window.addEventListener('click', block, true);
                window.setTimeout(() => window.removeEventListener('click', block, true), 400);
            }
        };

        el.addEventListener('mousedown', onDown);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', endDrag);

        return () => {
            el.removeEventListener('mousedown', onDown);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', endDrag);
        };
    }, [active]);

    return ref;
}

function HorizontalNarrowPosterRowSkeleton({ count = 6 }: { count?: number }) {
    return (
        <div className="relative -mx-4 md:-mx-8">
            <div className="pointer-events-none absolute bottom-4 left-0 top-0 z-10 w-16 bg-gradient-to-r from-[#000000] to-transparent" />
            <div className="pointer-events-none absolute bottom-4 right-0 top-0 z-10 w-16 bg-gradient-to-l from-[#000000] to-transparent" />
            <div className="overflow-x-auto overflow-y-hidden pl-8 pr-6 pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex gap-4 sm:gap-6">
                    {Array.from({ length: count }).map((_, i) => (
                        <div
                            key={i}
                            className="flex w-[160px] min-w-[160px] max-w-[160px] shrink-0 flex-col"
                        >
                            <div className="mb-3 aspect-[3/4] w-full animate-pulse rounded-xl border border-white/10 bg-neutral-900/40" />
                            <div className="h-3 w-full animate-pulse rounded bg-white/[0.06]" />
                            <div className="mt-1 h-3 w-2/3 animate-pulse rounded bg-white/[0.06]" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function FavoritadasRecentes({ formatoId = 5 }: FavoritadasRecentesProps) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [obras, setObras] = useState<unknown[]>([]);
    const [loading, setLoading] = useState(true);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const requestLimit = isMobile ? 10 : 24;
    const renderLimit = isMobile ? 10 : 24;
    const sectionAccent = FAVORITAS_SECTION_AMBER;
    const scrollRef = useHorizontalDragScroll<HTMLDivElement>(!loading && obras.length > 0);

    useEffect(() => {
        if (!user?.usr_id) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void api
            .get(`favorites/recent?limit=${requestLimit}&formato=${formatoId}`)
            .then((res) => {
                const anyRes = res.data as { data?: unknown } | unknown[] | undefined;
                const body = anyRes && typeof anyRes === 'object' && 'data' in anyRes ? (anyRes as { data: unknown }).data : anyRes;
                const nested = body && typeof body === 'object' && body !== null && 'data' in body ? (body as { data: unknown }).data : body;
                const arr = Array.isArray(nested) ? nested : Array.isArray(body) ? body : [];
                const filtered = arr.filter((o: unknown) => {
                    const ob = o as { formato?: { id?: number }; formt_id?: number };
                    const fid = ob.formato?.id ?? ob.formt_id;
                    return fid === formatoId;
                });
                if (!cancelled) setObras(filtered);
            })
            .catch(() => {
                if (!cancelled) setObras([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [user?.usr_id, formatoId, requestLimit]);

    useEffect(() => {
        const handleFavoriteChange = (event: CustomEvent) => {
            const { obraId, favoritada } = event.detail as { obraId: number; favoritada: boolean };
            setObras((prevObras) => {
                if (!favoritada) {
                    return prevObras.filter((obra) => (obra as { id: number }).id !== obraId);
                }
                return prevObras.map((obra) => {
                    const o = obra as { id: number; favoritada?: boolean };
                    return o.id === obraId ? { ...o, favoritada: true } : obra;
                });
            });
        };

        window.addEventListener('favoriteChanged', handleFavoriteChange as EventListener);
        return () => window.removeEventListener('favoriteChanged', handleFavoriteChange as EventListener);
    }, []);

    if (!loading && obras.length === 0) return null;

    return (
        <section className="relative animate-fadeIn py-4 delay-300 md:py-8">
            <div className="mb-6 flex items-center gap-3">
                <Heart className="h-6 w-6 shrink-0" style={{ color: sectionAccent }} />
                <h2 className="relative inline-block text-2xl font-bold text-white">
                    Favoritas Atualizadas
                    <span
                        className="absolute -bottom-2 left-0 h-1 w-1/3 rounded-full"
                        style={{ backgroundColor: sectionAccent }}
                    />
                </h2>
            </div>

            {loading && <HorizontalNarrowPosterRowSkeleton count={6} />}

            {!loading && (
                <div className="relative -mx-4 md:-mx-8">
                    <div className="pointer-events-none absolute bottom-4 left-0 top-0 z-10 w-16 bg-gradient-to-r from-[#000000] to-transparent" />
                    <div className="pointer-events-none absolute bottom-4 right-0 top-0 z-10 w-16 bg-gradient-to-l from-[#000000] to-transparent" />
                    <div
                        ref={scrollRef}
                        className="cursor-grab overflow-x-auto overflow-y-hidden pl-8 pr-6 pb-4 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden active:cursor-grabbing"
                    >
                        <div className="flex gap-4 sm:gap-6">
                            {obras.slice(0, renderLimit).map((item: unknown) => {
                                const it = item as { id: number; nome?: string; imagem?: string; favoritada?: boolean };
                                const isFavoritada = it.favoritada ?? false;
                                return (
                                    <div
                                        key={it.id}
                                        className="group flex w-[160px] min-w-[160px] max-w-[160px] shrink-0 flex-col"
                                        style={{ ['--accent' as string]: sectionAccent } as CSSProperties}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => navigate(generateWorkLink(it.id))}
                                            className="relative mb-3 aspect-[3/4] w-full shrink-0 overflow-hidden rounded-xl transition-transform duration-100 active:scale-95"
                                        >
                                            <img
                                                src={getObraImageUrl(it.id, it.imagem) || FALLBACK_IMAGE_URL}
                                                alt=""
                                                draggable={false}
                                                loading="eager"
                                                decoding="async"
                                                onError={(e) => applyFallbackImageOnError(e.currentTarget)}
                                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                            <div className="pointer-events-none absolute -left-3 -top-3 h-28 w-28 rounded-full bg-gradient-to-br from-amber-300/50 via-yellow-400/25 to-transparent blur-[2px] transition-colors duration-300 group-hover:from-amber-200/60 group-hover:via-amber-300/35" />
                                            {isFavoritada && (
                                                <div className="absolute left-2 top-2 z-10 rounded-md border border-amber-300/50 bg-gradient-to-br from-amber-400 to-yellow-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-900 shadow-lg shadow-amber-900/30">
                                                    Favoritado
                                                </div>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate(generateWorkLink(it.id))}
                                            className="mb-2 flex min-h-[2rem] items-start text-left"
                                        >
                                            <h3 className="line-clamp-2 text-xs font-bold leading-tight text-white transition-colors group-hover:[color:var(--accent)]">
                                                {it.nome}
                                            </h3>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
